import { css } from '@compiled/react';
import { isRichTextValueConsideredEmpty } from '@uniformdev/richtext';

import { RichTextComponent } from '@/libraries/design/utils';
import { TypographyStyles } from '@/libraries/wpl/foundations/src/typography-styles';

type TextAlignment = 'left' | 'center' | 'right';

export interface UniformTextProps {
	text?: string;
	richText?: string; // for rich text from Contentful
	uniformRichText?: unknown;
	style:
		| 'body-lg'
		| 'body-md'
		| 'body-sm'
		| 'body-lg bold'
		| 'body-md bold'
		| 'body-sm bold'
		| 'card-title'
		| 'card-title-sm'
		| 'fine-print'
		| 'fine-print-sm'
		| 'eyebrow-lg'
		| 'eyebrow-md'
		| 'eyebrow-sm'
		| 'callout'
		| 'callout-lg';
	alignment?: TextAlignment;
	tagType?: 'p' | 'dt' | 'dd' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

export const UniformText = ({
	text,
	richText,
	uniformRichText,
	style = 'body-lg',
	alignment = 'left',
	tagType,
}: UniformTextProps) => {
	const hasRichText = Boolean(richText) || !isRichTextValueConsideredEmpty(uniformRichText);
	// defaults to p when no tagType is provided
	// Use div instead of p when rich text is present to avoid nested <p> tags
	const Component = hasRichText && (!tagType || tagType === 'p') ? 'div' : tagType || 'p';

	const alignmentStyle = css({
		textAlign: alignment,
	});
	const boldStyling = css({
		'&.bold': {
			fontWeight: 700,
		},
	});
	const resetMargin = css({
		margin: 0,
	});
	const colorStyle = css({
		color: 'var(--wac-color-body)',
	});
	// When rich text is rendered, its paragraphs need to inherit typography from
	// the style class on the wrapper instead of the global paragraph defaults.
	const richTextInheritCss = css({
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& p': {
			fontSize: 'inherit',
			lineHeight: 'inherit',
			letterSpacing: 'inherit',
			fontFamily: 'inherit',
		},
	});

	return (
		<TypographyStyles>
			<Component
				className={style}
				css={[
					style?.includes('bold') && boldStyling,
					resetMargin,
					alignmentStyle,
					hasRichText && richTextInheritCss,
					colorStyle,
				]}
			>
				{richText ? (
					<RichTextComponent text={richText} />
				) : !isRichTextValueConsideredEmpty(uniformRichText) ? (
					<RichTextComponent uniformParameter="uniformRichText" />
				) : (
					text
				)}
			</Component>
		</TypographyStyles>
	);
};

export default UniformText;
